import React, { useState, useEffect } from "react";
import { generateClient } from "aws-amplify/data";
import { getUrl, uploadData, remove } from "aws-amplify/storage";
import Layout from "../../components/layout/AdminLayout.jsx";
import AuthInterceptor from "../../interceptors/authInterceptor.jsx";

const client = generateClient();

export default function VideoManager() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTag, setSearchTag] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // -- ESTADOS PARA PAGINACIÓN CON NEXT TOKEN --
  const [pageTokens, setPageTokens] = useState([null]); // El primer "token" es null (página inicial)
  const [currentPageIndex, setCurrentPageIndex] = useState(1); // Página 1 basada en índice 1
  const [hasMorePages, setHasMorePages] = useState(true);

  const pageSize = 5;

  const [editingVideo, setEditingVideo] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    tags: "",
    isFree: true,
    cost: 0,
    videoFile: null,
    thumbnailFile: null,
  });

  useEffect(() => {
    const doInitialFetch = async () => {
      setPageTokens([null]);
      setCurrentPageIndex(1);
      setHasMorePages(true);

      // Cargar la primera página (sin token)
      const newToken = await fetchVideos(null);
      if (newToken) {
        setPageTokens([null, newToken]);
      } else {
        // No hay un nextToken, así que no hay más páginas
        setHasMorePages(false);
      }
    };

    doInitialFetch();
  }, [searchTag]);

  // Obtener videos desde la base de datos
  const fetchVideos = async (token) => {
    try {
      setLoading(true);

      console.log("Buscando videos con etiqueta:", searchTag);
      console.log("Usando nextToken:", token);

      const opts = {};
      const q = searchTag ? { tags: { contains: searchTag } } : undefined;
      if (q) opts.filter = q;

      // const l = pageSize;
      // if (l) opts.limit = l;

      const n = token;
      if (n) opts.nextToken = n;

      console.log("Opciones de consulta:", opts);

      const { data: videoItems, nextToken } =
        await client.models.Conferencia.list({
          authMode: "userPool",
        });

      console.log("Videos obtenidos:", videoItems);

      const videosWithUrls = await Promise.all(
        (videoItems || []).map(async (video) => {
          const { url } = await getUrl({ path: video.thumbnailPath });
          return { ...video, thumbnailUrl: url.href };
        }),
      );
      setVideos(videosWithUrls);

      if (!nextToken) {
        // Si no hay nextToken, significa que ya no hay más páginas
        setHasMorePages(false);
      }

      return nextToken;
    } catch (error) {
      console.error("Error al obtener videos:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleNextPage = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      if (currentPageIndex < pageTokens.length - 1) {
        const token = pageTokens[currentPageIndex];
        await fetchVideos(token);
        setCurrentPageIndex((prev) => prev + 1);
        return;
      }
      // Si aún hay más páginas y estamos en la página actual más alta conocida...
      if (hasMorePages && currentPageIndex === pageTokens.length - 1) {
        const lastToken = pageTokens[pageTokens.length - 1];
        const newToken = await fetchVideos(lastToken);

        // Si la API nos devolvió un nuevo token, lo agregamos al array de tokens
        if (newToken) {
          setPageTokens((prev) => [...prev, newToken]);
        } else {
          setHasMorePages(false);
        }
      }

      setCurrentPageIndex((prev) => {
        const newIndex = prev + 1;
        return newIndex;
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePreviousPage = async () => {
    if (isProcessing) return;
    setIsProcessing(true);

    try {
      if (currentPageIndex > 1) {
        const newIndex = currentPageIndex - 1;
        const prevToken = pageTokens[newIndex - 1]; // Página es 1-based, array es 0-based
        const nextToken = await fetchVideos(prevToken);
        setCurrentPageIndex(newIndex);

        if (!nextToken) {
          setHasMorePages(false);
        } else {
          setHasMorePages(true);
        }
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleChangePage = async (pageIndex) => {
    // Si la página que pidió el usuario no existe aún, no hacemos nada.
    if (pageIndex > pageTokens.length) return;

    // Tomar el token de esa página y pedir los videos correspondientes
    const token = pageTokens[pageIndex - 1]; // Ajuste 1-based
    await fetchVideos(token);
    setCurrentPageIndex(pageIndex);
  };

  // Eliminar un video
  const handleDelete = async (videoId, videoPath, thumbnailPath) => {
    if (window.confirm("¿Seguro que deseas eliminar este video?")) {
      try {
        await client.models.Conferencia.delete(
          { id: videoId },
          { authMode: "userPool" },
        ); // Eliminar de la base de datos
        await remove({ path: videoPath }); // Eliminar el video de S3
        await remove({ path: thumbnailPath }); // Eliminar la miniatura de S3
        fetchVideos();
      } catch (error) {
        console.error("Error al eliminar el video:", error);
        alert("Error al eliminar el video.");
      }
    }
  };

  // Manejar edición de video
  const handleEdit = (video) => {
    setEditingVideo(video);
    setFormData({
      title: video.title || "",
      description: video.description || "",
      tags: video.tags || "",
      isFree: video.isFree || false,
      cost: video.cost || 0,
      videoFile: null,
      thumbnailFile: null,
    });
    setIsModalOpen(true);
  };

  // Guardar cambios en un video (actualiza solo si hay archivos nuevos)
  const handleSaveChanges = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      let videoPath = editingVideo.videoPath;
      let thumbnailPath = editingVideo.thumbnailPath;

      if (formData.videoFile) {
        const folder = formData.isFree ? "publico" : "privado";
        videoPath = `conferencias/${folder}/${formData.videoFile.name}`;
        await uploadData({ path: videoPath, data: formData.videoFile }).result;
      }

      if (formData.thumbnailFile) {
        thumbnailPath = `conferencias/miniaturas/${formData.thumbnailFile.name}`;
        await uploadData({
          path: thumbnailPath,
          data: formData.thumbnailFile,
        }).result;
      }

      await client.models.Conferencia.update(
        {
          id: editingVideo.id,
          title: formData.title,
          description: formData.description,
          tags: formData.tags,
          isFree: formData.isFree,
          cost: formData.isFree ? 0 : parseFloat(formData.cost),
          videoPath,
          thumbnailPath,
        },
        { authMode: "userPool" },
      );

      setEditingVideo(null);
      setIsModalOpen(false);
      fetchVideos();
    } catch (error) {
      console.error("Error al actualizar el video:", error);
      alert("Error al actualizar el video.");
    } finally {
      setLoading(false);
    }
  };

  // Abrir modal para agregar un nuevo video
  const openCreateModal = () => {
    setFormData({
      title: "",
      description: "",
      tags: "",
      isFree: true,
      cost: 0,
      videoFile: null,
      thumbnailFile: null,
    });
    setIsModalOpen(true);
  };

  // Manejar cambios en los campos del formulario
  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Guardar nuevo video
  const handleCreateVideo = async (e) => {
    e.preventDefault();

    const { title, description, tags, isFree, cost, videoFile, thumbnailFile } =
      formData;

    if (!title || !description || !videoFile || !thumbnailFile) {
      alert("Por favor completa todos los campos requeridos.");
      return;
    }

    try {
      setLoading(true); // Mostrar spinner

      // Definir rutas para S3
      const folder = isFree ? "publico" : "privado";
      const videoPath = `conferencias/${folder}/${videoFile.name}`;
      const thumbnailPath = `conferencias/miniaturas/${thumbnailFile.name}`;

      // Subir archivos a S3
      const resultVideo = await uploadData({ path: videoPath, data: videoFile })
        .result;
      const resultImage = await uploadData({
        path: thumbnailPath,
        data: thumbnailFile,
      }).result;

      // Guardar información en la base de datos
      await client.models.Conferencia.create(
        {
          title,
          description,
          tags: tags.trim(),
          isFree,
          cost: isFree ? 0 : parseFloat(cost),
          videoPath,
          thumbnailPath,
        },
        {
          authMode: "userPool",
        },
      );

      fetchVideos();
      setIsModalOpen(false);
    } catch (error) {
      console.error("Error al cargar el video:", error);
      alert("Error al cargar el video.");
    } finally {
      setLoading(false); // Ocultar spinner
    }
  };

  return (
    <Layout>
      <AuthInterceptor>
        <div className="p-4">
          <h2 className="mb-6 text-2xl font-bold">Gestión de Videos</h2>

          {/* Botón para abrir modal */}
          <button
            onClick={openCreateModal}
            className="mb-4 rounded bg-green-500 px-4 py-2 text-white hover:bg-green-600"
          >
            Agregar Nuevo Video
          </button>

          {/* Tabla de videos */}
          <div className="overflow-x-auto rounded-lg shadow-md">
            <table className="w-full text-left text-sm text-gray-700">
              <thead className="bg-gray-200 text-gray-700">
                <tr>
                  <th className="px-4 py-2">Miniatura</th>
                  <th className="px-4 py-2">Título</th>
                  <th className="px-4 py-2">Descripción</th>
                  <th className="px-4 py-2">Etiquetas</th>
                  <th className="px-4 py-2">Tipo</th>
                  {/* <th className="px-4 py-2">Costo</th> */}
                  <th className="px-4 py-2">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {videos &&
                  videos.map((video) => (
                    <tr key={video.id} className="border-b">
                      <td className="px-4 py-2">
                        <img
                          src={video.thumbnailUrl}
                          alt={video.title}
                          className="h-20 w-32 rounded"
                        />
                      </td>
                      <td className="px-4 py-2">{video.title}</td>
                      <td className="px-4 py-2">{video.description}</td>
                      <td className="px-4 py-2">{video.tags}</td>
                      <td className="px-4 py-2">
                        {video.isFree ? "Público" : "Privado"}
                      </td>
                      {/* <td className="px-4 py-2">
                        {video.isFree ? "Gratis" : `$${video.cost.toFixed(2)}`}
                      </td> */}
                      <td className="flex gap-2 px-4 py-2">
                        <button
                          onClick={() => handleEdit(video)}
                          className="rounded bg-yellow-500 px-2 py-1 text-white hover:bg-yellow-600"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() =>
                            handleDelete(
                              video.id,
                              video.videoPath,
                              video.thumbnailPath,
                            )
                          }
                          className="rounded bg-red-500 px-2 py-1 text-white hover:bg-red-600"
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  ))}
                {videos.length === 0 && (
                  <tr>
                    <td
                      colSpan="7"
                      className="px-4 py-2 text-center text-gray-500"
                    >
                      No hay videos disponibles.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Botones de Paginación manual */}
            <div className="my-4 flex items-center justify-center gap-4">
              <button
                onClick={handlePreviousPage}
                disabled={currentPageIndex === 1 || isProcessing}
                className="rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600 disabled:opacity-50"
              >
                Anterior
              </button>

              <span className="text-sm text-gray-700">
                Página: {currentPageIndex}
              </span>

              <button
                onClick={handleNextPage}
                disabled={!hasMorePages || isProcessing}
                className="rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600 disabled:opacity-50"
              >
                Siguiente
              </button>
            </div>
          </div>

          {/* Modal de creación/edición */}
          {isModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
              <div className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-lg">
                <h3 className="mb-4 text-lg font-bold">
                  {editingVideo ? "Editar Video" : "Agregar Nuevo Video"}
                </h3>
                <form
                  onSubmit={
                    editingVideo ? handleSaveChanges : handleCreateVideo
                  }
                >
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Título:
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) =>
                        handleFormChange("title", e.target.value)
                      }
                      className="w-full rounded border p-2"
                      required
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Descripción:
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) =>
                        handleFormChange("description", e.target.value)
                      }
                      className="w-full rounded border-gray-300 p-2"
                      required
                    ></textarea>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Etiquetas (separadas por comas):
                    </label>
                    <input
                      type="text"
                      value={formData.tags}
                      onChange={(e) => handleFormChange("tags", e.target.value)}
                      className="w-full rounded border-gray-300 p-2"
                      placeholder="Ejemplo: React, JavaScript, Conferencia"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Tipo de Video:
                    </label>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="isFree"
                          checked={formData.isFree}
                          onChange={() => {
                            handleFormChange("isFree", true);
                            handleFormChange("cost", 0);
                          }}
                        />
                        Público
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="isFree"
                          checked={!formData.isFree}
                          onChange={() => handleFormChange("isFree", false)}
                        />
                        Privado
                      </label>
                    </div>
                  </div>
                  {!formData.isFree && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700">
                        Costo:
                      </label>
                      <input
                        type="number"
                        value={formData.cost}
                        onChange={(e) =>
                          handleFormChange("cost", e.target.value)
                        }
                        className="w-full rounded border-gray-300 p-2"
                        min="0.01"
                        step="0.01"
                        required
                      />
                    </div>
                  )}
                  {/* Mostrar miniatura actual en modo edición */}
                  {editingVideo && (
                    <div className="mb-4">
                      <p className="text-sm font-medium text-gray-700">
                        Miniatura actual:
                      </p>
                      <img
                        src={editingVideo.thumbnailUrl}
                        alt={editingVideo.title}
                        className="mt-2 h-24 w-40 rounded"
                      />
                    </div>
                  )}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Archivo de Video:
                    </label>
                    <input
                      type="file"
                      accept="video/*"
                      onChange={(e) =>
                        handleFormChange("videoFile", e.target.files[0])
                      }
                      className="w-full rounded border-gray-300 p-2"
                      required={!editingVideo}
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Miniatura del Video:
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        handleFormChange("thumbnailFile", e.target.files[0])
                      }
                      className="w-full rounded border-gray-300 p-2"
                      required={!editingVideo}
                    />
                  </div>
                  <button
                    type="submit"
                    className="rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
                  >
                    Guardar
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsModalOpen(false);
                      setEditingVideo(null);
                    }}
                    className="ml-2 rounded bg-red-500 px-4 py-2 text-white hover:bg-red-600"
                  >
                    Cancelar
                  </button>
                </form>
                {loading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
                    <div className="h-10 w-10 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </AuthInterceptor>
    </Layout>
  );
}
